import { useState, useEffect } from "react";
import { useThemeContext } from "../theme/ThemeContext";
import { useLockBodyScroll } from "../hooks/useLockBodyScroll";
import { useEscapeKey } from "../hooks/useEscapeKey";
import { Button } from "./shared";
import { overlayBackdrop } from "../theme/styles";
import { dbLoadSCCOFull, dbAddSCCO, dbDeleteSCCO, dbUpdateSccoPassword, dbSetSccoAdmin } from "../db";
import { TOAST_DURATION_MS } from "../utils/constants";

export function SCCORosterModal({ onClose }) {
  const { SA, GLASS, tokens, TYPOGRAPHY, RADIUS } = useThemeContext();
  useLockBodyScroll();
  useEscapeKey(onClose);

  const [list,         setList]         = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [adding,       setAdding]       = useState(false);
  const [newName,      setNewName]      = useState("");
  const [newBadge,     setNewBadge]     = useState("");
  const [saving,       setSaving]       = useState(false);
  const [pwInputs,     setPwInputs]     = useState({});
  const [pwSaving,     setPwSaving]     = useState({});
  const [pwSaved,      setPwSaved]      = useState({});
  const [confirmDel,   setConfirmDel]   = useState(null);
  const [confirmAdmin, setConfirmAdmin] = useState(null);
  const [adminSaving,  setAdminSaving]  = useState({});

  useEffect(() => {
    dbLoadSCCOFull().then(rows => { setList(rows); setLoading(false); });
  }, []);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    const created = await dbAddSCCO(newName.trim(), newBadge.trim());
    if (created) setList(prev => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
    setNewName(""); setNewBadge(""); setAdding(false); setSaving(false);
  };

  const handleDelete = async (id) => {
    await dbDeleteSCCO(id);
    setList(prev => prev.filter(x => x.id !== id));
    setConfirmDel(null);
  };

  const handleSetPw = async (id) => {
    const val = (pwInputs[id] || "").trim();
    if (!val) return;
    setPwSaving(p => ({ ...p, [id]: true }));
    await dbUpdateSccoPassword(id, val);
    setPwInputs(p => ({ ...p, [id]: "" }));
    setPwSaved(p => ({ ...p, [id]: true }));
    setTimeout(() => setPwSaved(p => ({ ...p, [id]: false })), TOAST_DURATION_MS);
    setPwSaving(p => ({ ...p, [id]: false }));
  };

  const handleToggleAdmin = async (id, grant) => {
    setAdminSaving(p => ({ ...p, [id]: true }));
    await dbSetSccoAdmin(id, grant);
    setList(prev => prev.map(x => x.id === id ? { ...x, is_admin: grant } : x));
    setConfirmAdmin(null);
    setAdminSaving(p => ({ ...p, [id]: false }));
  };

  return (
    <div onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }} style={{ ...overlayBackdrop, zIndex: 300,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ ...GLASS.modal,
        width: "100%", maxWidth: 460, maxHeight: "88vh",
        borderRadius: 16, display: "flex", flexDirection: "column" }}>

        <div style={{ padding: "20px 22px 14px", borderBottom: `1px solid ${SA.borderGray}`,
          display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: SA.white }}>SCCO Roster</div>
            <div style={{ fontSize: TYPOGRAPHY.caption, color: SA.silver, marginTop: 2 }}>Manage agents & login passwords</div>
          </div>
          <Button variant="icon" onClick={onClose}
            style={{ background: SA.medGray, color: SA.silver, width: 28, height: 28, fontSize: 16 }}>×</Button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "6px 0" }}>
          {loading && <div style={{ padding: "32px", textAlign: "center", color: SA.borderGray, fontSize: TYPOGRAPHY.body }}>Loading…</div>}

          {!loading && list.map(s => (
            <div key={s.id} style={{ padding: "14px 20px", borderBottom: `1px solid ${SA.borderGray}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                <div style={{ width: 38, height: 38, borderRadius: RADIUS.circle, flexShrink: 0,
                  background: SA.darkGray, border: `2px solid ${SA.borderGray}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: TYPOGRAPHY.note, fontWeight: 700, color: SA.silver }}>
                  {s.badge || s.name.slice(0, 2).toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: TYPOGRAPHY.note, fontWeight: 600, color: SA.white }}>{s.name}</span>
                    {s.is_admin && (
                      <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.06em",
                        background: "rgba(200,168,48,0.15)", border: "1px solid #c8a830",
                        color: "#c8a830", borderRadius: RADIUS.pill, padding: "1px 6px" }}>
                        ADMIN
                      </span>
                    )}
                  </div>
                  {s.badge && <div style={{ fontSize: TYPOGRAPHY.label, color: SA.silver, marginTop: 1 }}>#{s.badge}</div>}
                </div>
                {confirmDel === s.id ? (
                  <div style={{ display: "flex", gap: 6 }}>
                    <Button variant="pill" onClick={() => setConfirmDel(null)}
                      style={{ padding: "5px 10px", fontSize: TYPOGRAPHY.label }}>
                      Cancel
                    </Button>
                    <Button onClick={() => handleDelete(s.id)}
                      style={{ padding: "5px 10px", borderRadius: RADIUS.pill, fontSize: TYPOGRAPHY.label, fontWeight: 700,
                        background: tokens.dangerBg, border: `1px solid #7A2626`, color: tokens.danger }}>
                      Confirm
                    </Button>
                  </div>
                ) : confirmAdmin?.id === s.id ? (
                  <div style={{ display: "flex", gap: 6 }}>
                    <Button variant="pill" onClick={() => setConfirmAdmin(null)}
                      style={{ padding: "5px 10px", fontSize: TYPOGRAPHY.label }}>
                      Cancel
                    </Button>
                    <Button onClick={() => handleToggleAdmin(s.id, confirmAdmin.grant)}
                      disabled={adminSaving[s.id]}
                      style={{ padding: "5px 10px", borderRadius: RADIUS.pill, fontSize: TYPOGRAPHY.label, fontWeight: 700,
                        background: confirmAdmin.grant ? "rgba(200,168,48,0.15)" : tokens.dangerBg,
                        border: `1px solid ${confirmAdmin.grant ? "#c8a830" : "#7A2626"}`,
                        color: confirmAdmin.grant ? "#c8a830" : tokens.danger }}>
                      {adminSaving[s.id] ? "…" : "Confirm"}
                    </Button>
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: 6 }}>
                    <Button onClick={() => { setConfirmDel(null); setConfirmAdmin({ id: s.id, grant: !s.is_admin }); }}
                      style={{ padding: "5px 10px", borderRadius: RADIUS.pill, fontSize: TYPOGRAPHY.label, fontWeight: 600,
                        background: s.is_admin ? tokens.dangerBgSubtle : "rgba(200,168,48,0.10)",
                        border: `1px solid ${s.is_admin ? "#7A2626" : "#c8a830"}`,
                        color: s.is_admin ? tokens.danger : "#c8a830" }}>
                      {s.is_admin ? "Revoke Admin" : "Make Admin"}
                    </Button>
                    <Button onClick={() => { setConfirmAdmin(null); setConfirmDel(s.id); }}
                      style={{ padding: "5px 12px", borderRadius: RADIUS.pill, fontSize: TYPOGRAPHY.label, fontWeight: 600,
                        background: tokens.dangerBgSubtle, border: `1px solid #7A2626`, color: tokens.danger }}>
                      Delete
                    </Button>
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <input type="password" placeholder="Set login password…"
                  value={pwInputs[s.id] || ""}
                  onChange={e => setPwInputs(p => ({ ...p, [s.id]: e.target.value }))}
                  onKeyDown={e => e.key === "Enter" && handleSetPw(s.id)}
                  style={{ flex: 1, background: SA.darkGray, border: `1px solid ${SA.borderGray}`,
                    borderRadius: RADIUS.sm, color: SA.white, padding: "8px 12px", fontSize: TYPOGRAPHY.body,
                    fontFamily: "'Barlow',sans-serif", outline: "none" }} />
                <Button onClick={() => handleSetPw(s.id)}
                  disabled={!pwInputs[s.id]?.trim() || pwSaving[s.id]}
                  style={{ padding: "8px 14px", borderRadius: RADIUS.md, fontSize: TYPOGRAPHY.caption, fontWeight: 600,
                    background: pwSaved[s.id] ? "rgba(110,196,114,0.15)" : SA.medGray,
                    border: `1px solid ${pwSaved[s.id] ? "#2E6B30" : SA.borderGray}`,
                    color: pwSaved[s.id] ? tokens.success : SA.lightSilver, transition: "all 0.2s" }}>
                  {pwSaved[s.id] ? "✓ Saved" : pwSaving[s.id] ? "…" : "Set"}
                </Button>
              </div>
            </div>
          ))}

          {!loading && (adding ? (
            <div style={{ margin: "12px 16px", background: SA.darkGray, borderRadius: 12,
              border: `1px solid ${SA.borderGray}`, padding: "14px 16px" }}>
              <div style={{ fontSize: TYPOGRAPHY.caption, color: SA.silver, marginBottom: 10, fontWeight: 500 }}>New Agent</div>
              <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Full name"
                style={{ width: "100%", background: SA.medGray, border: `1px solid ${SA.borderGray}`,
                  borderRadius: RADIUS.sm, color: SA.white, padding: "9px 12px", fontSize: TYPOGRAPHY.note,
                  fontFamily: "'Barlow',sans-serif", outline: "none", marginBottom: 8 }} />
              <input value={newBadge} onChange={e => setNewBadge(e.target.value.toUpperCase().slice(0, 6))}
                placeholder="Badge / ID (optional)"
                style={{ width: "100%", background: SA.medGray, border: `1px solid ${SA.borderGray}`,
                  borderRadius: RADIUS.sm, color: SA.white, padding: "9px 12px", fontSize: TYPOGRAPHY.note,
                  fontFamily: "'Barlow',sans-serif", outline: "none", marginBottom: 10 }} />
              <div style={{ display: "flex", gap: 8 }}>
                <Button onClick={handleAdd} disabled={!newName.trim() || saving}
                  style={{ flex: 1, padding: "9px", borderRadius: RADIUS.md, fontSize: TYPOGRAPHY.body, fontWeight: 600,
                    background: newName.trim() ? SA.accent : "transparent",
                    border: `1px solid ${newName.trim() ? SA.accent : SA.borderGray}`,
                    color: newName.trim() ? "#fff" : SA.borderGray }}>
                  {saving ? "Saving…" : "Add Agent"}
                </Button>
                <Button variant="secondary" onClick={() => { setAdding(false); setNewName(""); setNewBadge(""); }}
                  style={{ padding: "9px 14px", borderRadius: RADIUS.md, fontSize: TYPOGRAPHY.body }}>Cancel</Button>
              </div>
            </div>
          ) : (
            <Button variant="tile" onClick={() => setAdding(true)}
              style={{ width: "100%", padding: "14px 20px", gap: 12, textAlign: "left",
                borderTop: list.length ? `1px solid ${SA.borderGray}` : "none", marginTop: 4 }}>
              <div style={{ width: 38, height: 38, borderRadius: RADIUS.circle, background: "transparent",
                border: `1.5px dashed ${SA.borderGray}`, display: "flex", alignItems: "center",
                justifyContent: "center", fontSize: 20, color: SA.borderGray, flexShrink: 0 }}>+</div>
              <span style={{ fontSize: TYPOGRAPHY.body, color: SA.silver, fontWeight: 500 }}>Add new agent</span>
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}
